import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import {components} from 'react-select';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import NotificationModal from "../common/notificationmodal";
import WarningIcon from "@mui/icons-material/Warning";
import { College } from "./types";

interface MultiSelectDropdownProps {
  selectedColleges: College[];
  handleSelectChange: (selected: College[]) => void;
  handleSearch: (search: string) => Promise<College[]>;
  handleRemove: (id: string) => void;
}

const ClearIndicator = () => null;

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  selectedColleges,
  handleSelectChange,
  handleSearch,
  handleRemove,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Handle drag-and-drop reordering
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(selectedColleges);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    handleSelectChange(reordered);
  };

  // Limit to 4 selections and notify the user
  const handleSelection = (selected: College[]) => {
    if (selected.length > 4) {
      setIsModalOpen(true);
      return;
    }
    handleSelectChange(selected);
  };

  // Fetch options for AsyncSelect
  const promiseOptions = async (inputValue: string) => {
    setInputValue(inputValue);
    const results = await handleSearch(inputValue);
    return results.filter(
      (option) => !selectedColleges.some((selected) => selected.id === option.id)
    ).map((option) => ({
      ...option,
      value: option.id,
      label: (
        <span>
          {option.label}
          <div style={{ fontSize: '0.8em', color: '#33333',paddingLeft:"15px" }}>
            {option.city}, {option.state}. ACT Code: {option.id}
          </div>
        </span>
      ),
    })
    );
  };

  //console.log("selectedColleges:", selectedColleges);

  // Function to extract text from React elements
  function extractTextFromReactElement(element: React.ReactNode): string {
    if (typeof element === 'string') {
      return element;
    }
    if (Array.isArray(element)) {
      return element.map(extractTextFromReactElement).join(' ');
    }
    if (React.isValidElement(element)) {
      return extractTextFromReactElement(element.props.children);
    }
    return '';
  }


  return (
    <div>
      {/* Async Select Box */}
      <AsyncSelect
        isMulti
        cacheOptions={false}
        loadOptions={promiseOptions}
        onChange={(selected) => {
          handleSelection(selected as College[])
        }}
        value={selectedColleges}
        placeholder="Type to search colleges..."
        controlShouldRenderValue={false} // Hide selected items from the input box
        aria-label="College search input"
        components={{ClearIndicator}}
        backspaceRemovesValue={false}
      />

      {/* Drag-and-Drop Selected Items */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="selected-colleges">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                marginTop: "20px",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                background: "#f9f9f9",
                minHeight: "100px",
              }}
            >
              {selectedColleges.map((college, index) => (
                <Draggable
                  key={college.id}
                  draggableId={college.id}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px",
                        marginBottom: "8px",
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        ...provided.draggableProps.style,
                      }}
                      aria-label={`${index + 1}. ${extractTextFromReactElement(college.label)}`}                    >
                      <span>
                        {index + 1}. {college.label}
                      </span>
                      <button
                        onClick={() => handleRemove(college.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "red",
                          cursor: "pointer",
                        }}
                        aria-label={`Remove`}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Modal Notification for Exceeding Limit */}
      <NotificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        message="You can select up to 4 score recipients only."
        buttonText="OK"
      />
    </div>
  );
};

export default MultiSelectDropdown;
